import { type FormEvent, useState } from "react";
import { Navigate, useNavigate, useSearchParams } from "react-router-dom";
import { GoogleMark } from "@/components/google-mark";
import { useAuth } from "@/src/auth-context";
import { authErrorMessage } from "@/src/auth-errors";
import { useDocumentMeta } from "@/src/use-document-meta";

export function LoginPage() {
  const { user, loading, signInWithEmail, createAccount, signInWithGoogle } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [mode, setMode] = useState<"signin" | "create">("signin");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const returnTo = safeReturnPath(searchParams.get("return_to"));

  useDocumentMeta({
    title: "Sign in | Chuno",
    description: "Sign in to your Chuno account.",
    robots: "noindex,nofollow",
    canonical: "https://www.chuno.work/login",
  });

  if (loading) return <main id="main" className="page-loading"><div><span />Checking your account…</div></main>;
  if (user) return <Navigate to={returnTo} replace />;

  const submit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setBusy(true);
    setError("");
    try {
      if (mode === "signin") await signInWithEmail(email.trim(), password);
      else await createAccount(email.trim(), password);
      navigate(returnTo, { replace: true });
    } catch (caught) {
      setError(authErrorMessage(caught));
    } finally {
      setBusy(false);
    }
  };

  return (
    <main id="main" className="login-page">
      <section className="login-page__story">
        <div>
          <p className="eyebrow"><span /> Your Chuno account</p>
          <h1>One place for your job search.</h1>
          <p>Keep your account, activity, and preferences together as you explore opportunities.</p>
        </div>
      </section>
      <section className="login-page__form-wrap" aria-labelledby="login-title">
        <div className="login-card">
          <p className="auth-dialog__eyebrow">Welcome to Chuno</p>
          <h2 id="login-title">{mode === "signin" ? "Sign in." : "Create your account."}</h2>
          <p className="auth-dialog__intro">Use Google or continue with your email address.</p>
          <button className="auth-google" type="button" disabled={busy} onClick={() => {
            setBusy(true);
            setError("");
            void signInWithGoogle(returnTo).catch((caught) => {
              setError(authErrorMessage(caught));
              setBusy(false);
            });
          }}><GoogleMark /> Continue with Google</button>
          <div className="auth-divider"><span>or use email</span></div>
          <form className="auth-form" onSubmit={submit}>
            <label><span>Email address</span><input type="email" autoComplete="email" value={email} onChange={(event) => setEmail(event.target.value)} required disabled={busy} /></label>
            <label><span>Password</span><input type="password" autoComplete={mode === "signin" ? "current-password" : "new-password"} value={password} onChange={(event) => setPassword(event.target.value)} minLength={6} required disabled={busy} /></label>
            {error ? <p className="auth-error" role="alert">{error}</p> : null}
            <button className="button button--dark auth-submit" type="submit" disabled={busy}>{busy ? "Please wait…" : mode === "signin" ? "Sign in" : "Create account"}</button>
          </form>
          <p className="auth-switch">{mode === "signin" ? "New to Chuno?" : "Already have an account?"}{" "}<button type="button" disabled={busy} onClick={() => { setMode((current) => current === "signin" ? "create" : "signin"); setError(""); }}>{mode === "signin" ? "Create account" : "Sign in"}</button></p>
        </div>
      </section>
    </main>
  );
}

function safeReturnPath(value: string | null) {
  return value?.startsWith("/") && !value.startsWith("//") ? value : "/dashboard";
}
